import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Divider,
    Alert, CircularProgress, Snackbar, Avatar, Tooltip, IconButton, Chip,
    useTheme, useMediaQuery
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import api from '../services/api';
import { validarSessao } from '../services/api';
import FotoCropModal from './FotoCropModal';
import { getDecodedToken, getIdUsuario } from '../services/auth';

const ROLE_LABELS = {
    ROLE_ADMIN: { label: 'Administrador', color: 'error' },
    ROLE_USUARIO: { label: 'Usuário', color: 'primary' },
    ROLE_VISUALIZADOR: { label: 'Visualizador', color: 'default' },
};

export default function PerfilUsuario() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const decoded = getDecodedToken();
    const username = decoded?.sub || '';
    const idUsuario = getIdUsuario();
    const idPsicologo = decoded?.id_psicologo || null;
    const nomeCompleto = decoded?.nome_completo || username;
    const role = decoded?.role || '';
    const roleInfo = ROLE_LABELS[role] || { label: role, color: 'default' };

    const [fotoPreview, setFotoPreview] = useState(localStorage.getItem('user_foto_url') || null);

    useEffect(() => {
        validarSessao().then(data => {
            if (data?.fotoUrl) {
                setFotoPreview(data.fotoUrl);
                localStorage.setItem('user_foto_url', data.fotoUrl);
            }
        }).catch(() => {});
    }, []);
    const [uploadingFoto, setUploadingFoto] = useState(false);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const fotoInputRef = useRef(null);

    const [novaSenha, setNovaSenha] = useState('');
    const [confirmarSenha, setConfirmarSenha] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleFotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        setCropImageSrc(url);
        setCropModalOpen(true);
        e.target.value = '';
    };

    const handleCropConfirm = async (blob) => {
        setCropModalOpen(false);
        setUploadingFoto(true);
        try {
            const form = new FormData();
            form.append('arquivo', blob, 'foto.jpg');
            const res = await api.post(`/usuarios/${idUsuario}/foto`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const novaUrl = res.data.fotoURL;
            setFotoPreview(novaUrl);
            localStorage.setItem('user_foto_url', novaUrl);
            window.dispatchEvent(new Event('profilePhotoUpdated'));
            setSnackbar({ open: true, message: 'Foto atualizada com sucesso!', severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: 'Erro ao fazer upload da foto.', severity: 'error' });
        } finally {
            setUploadingFoto(false);
            if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
            setCropImageSrc(null);
        }
    };

    const handleCropClose = () => {
        setCropModalOpen(false);
        if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
        setCropImageSrc(null);
    };

    const handleSalvar = async () => {
        setError('');
        if (novaSenha !== confirmarSenha) {
            setError('As senhas não coincidem.');
            return;
        }
        if (novaSenha.length < 6) {
            setError('Nova senha deve ter no mínimo 6 caracteres.');
            return;
        }
        try {
            setLoading(true);
            await api.put(`/usuarios/${idUsuario}`, {
                usuario: username,
                senha: novaSenha,
                role: null,
                id_psicologo: idPsicologo,
                ativo: null
            });
            setNovaSenha('');
            setConfirmarSenha('');
            setSnackbar({ open: true, message: 'Senha alterada com sucesso!', severity: 'success' });
        } catch {
            setError('Erro ao alterar senha. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const SectionTitle = ({ children }) => (
        <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
            {children}
        </Typography>
    );

    return (
        <Box sx={{ flexGrow: 1, px: isMobile ? 1 : 0 }}>
            {/* CABEÇALHO */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <PersonOutlinedIcon sx={{ fontSize: 32, color: '#1ABB9C' }} />
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                    Meu Perfil
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>

                {/* CARD ESQUERDO: IDENTIDADE */}
                <Paper elevation={0} sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar
                                src={fotoPreview || undefined}
                                sx={{ width: 96, height: 96, bgcolor: '#1ABB9C', fontSize: 38, fontWeight: 700 }}
                            >
                                {!fotoPreview && nomeCompleto.charAt(0).toUpperCase()}
                            </Avatar>
                            <Tooltip title="Alterar foto">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => fotoInputRef.current?.click()}
                                        disabled={uploadingFoto}
                                        sx={{ position: 'absolute', bottom: -4, right: -4, bgcolor: '#1ABB9C', color: '#fff', '&:hover': { bgcolor: '#129A80' }, width: 28, height: 28 }}
                                    >
                                        {uploadingFoto ? <CircularProgress size={14} color="inherit" /> : <PhotoCameraIcon sx={{ fontSize: 15 }} />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <input ref={fotoInputRef} type="file" accept="image/*" hidden onChange={handleFotoChange} />
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#333' }}>
                                {nomeCompleto}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                @{username}
                            </Typography>
                            <Chip
                                label={roleInfo.label}
                                color={roleInfo.color}
                                size="small"
                                variant="outlined"
                            />
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                            JPG, PNG ou WebP. Máx. 5 MB.
                        </Typography>
                    </Box>
                </Paper>

                {/* CARD DIREITO: CONFIGURAÇÕES */}
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', flex: 1, width: '100%' }}>

                    <SectionTitle>Acesso ao Sistema</SectionTitle>

                    <TextField
                        label="Login"
                        value={username}
                        fullWidth
                        disabled
                        sx={{ mb: 3 }}
                    />

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle>Segurança</SectionTitle>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
                        <TextField
                            label="Nova Senha"
                            type="password"
                            value={novaSenha}
                            onChange={e => setNovaSenha(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Confirmar Nova Senha"
                            type="password"
                            value={confirmarSenha}
                            onChange={e => setConfirmarSenha(e.target.value)}
                            fullWidth
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LockOutlinedIcon />}
                            onClick={handleSalvar}
                            disabled={loading || !novaSenha || !confirmarSenha}
                            sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 4 }}
                        >
                            Alterar Senha
                        </Button>
                    </Box>
                </Paper>
            </Box>

            <FotoCropModal
                open={cropModalOpen}
                imageSrc={cropImageSrc}
                onClose={handleCropClose}
                onConfirm={handleCropConfirm}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
